import { Component, EventEmitter, Input, Output } from '@angular/core';
import { of } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  APPOINTMENT_STATUS,
  Appointment,
  BusinessObject,
  Staff,
  APP_MODE,
  ABLE_TO_CHECKIN_BEFORE_MINUTES,
} from '../../../models';
import {
  BusinessObjectUtils,
  CustomerUtils,
  DateTimeUtils,
} from '../../../utils';
import { PipesModule } from '../../../pipes';
import { LibCoreLayoutModule } from '../../../ui/core-layout';
import { CalendarAppointmentDetailsComponent } from '../calendar-appointment-details/calendar-appointment-details.component';
import { CoreCalendarEntryComponent } from '../core/core-calendar-entry.component';
import { CalendarStorageService } from '../services/calendar-storage.service';

@Component({
  selector: 'lib-calendar-appointment-details-card',
  templateUrl: './calendar-appointment-details-card.component.html',
  styleUrls: ['./calendar-appointment-details-card.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PipesModule,
    LibCoreLayoutModule,
    CalendarAppointmentDetailsComponent,
  ],
})
export class CalendarAppointmentDetailsCardComponent extends CoreCalendarEntryComponent {
  private _businessObject: BusinessObject;
  @Input() set businessObject(value: BusinessObject) {
    this._businessObject = value;
    this.isProVersion = BusinessObjectUtils.isProVersion(value);
  }
  get businessObject(): BusinessObject {
    return this._businessObject;
  }

  private _appointment: Appointment;
  @Input() set appointment(value: Appointment) {
    this._appointment = value;

    this.setCountDownForCheckin(this.appointment);
    this.isCheckedIn = CustomerUtils.isAppointmentCheckedIn(this.appointment);
    this.isCancelled = CustomerUtils.isAppointmentCancelled(this.appointment);
  }
  get appointment(): Appointment {
    return this._appointment;
  }

  @Input() staff: Staff;
  @Input() isLoading = false;
  @Input() disableCheckin: boolean;

  @Output() checkinClicked = new EventEmitter<void>();

  public readonly APPOINTMENT_STATUS = APPOINTMENT_STATUS;

  public isProVersion: boolean;
  public isCheckInTime: boolean;
  public isCheckedIn: boolean;
  public isCancelled: boolean;

  private readonly TIMEOUT_LIMIT = 2147483647; // ms

  constructor(protected calendarStorageService: CalendarStorageService) {
    super(calendarStorageService);
  }

  public isEditActionForbiddenForStaffApp(): boolean {
    return CustomerUtils.isEditActionForbiddenForStaffApp(
      this.mode,
      this.appointment?.staffId,
      this.calendarAdditionalConfig?.primaryStaffId,
      this.calendarAdditionalConfig?.forbiddenActions
    );
  }

  public shouldDisplayAdvancedInfo(): boolean {
    return this.mode !== APP_MODE.BOOKING && this.mode !== APP_MODE.CHECKIN;
  }

  public checkinClickHandler(): void {
    if (this.isLoading || !this.isCheckInTime || !this.isProVersion) {
      return;
    }
    this.checkinClicked.emit();
  }

  private setCountDownForCheckin(appointment: Appointment) {
    if (appointment?.status !== APPOINTMENT_STATUS.CONFIRMED) {
      this.isCheckInTime = false;
      return;
    }

    const currentTime = new Date();
    const appointmentStartTime = DateTimeUtils.parseStringToDate(
      appointment.startTime
    );

    const checkinTime =
      appointmentStartTime.getTime() -
      ABLE_TO_CHECKIN_BEFORE_MINUTES * 60 * 1000;

    const timeToWait = checkinTime - currentTime.getTime();

    if (timeToWait < this.TIMEOUT_LIMIT) {
      if (timeToWait > 0) {
        of('')
          .pipe(takeUntil(this.componentDestroyed), delay(timeToWait))
          .subscribe(() => (this.isCheckInTime = true));
      } else {
        this.isCheckInTime = true;
      }
    }
  }
}
